/**
 * AFA (All-Flash Array) storage node metrics calculator
 * Calculates all metrics specific to AFA (F) nodes with flash-only storage
 */

import { getCompressionState, calculateWriteEfficiency, calculateThroughput, calculateIops, resolveNetworkThroughput } from '../shared/performanceHelpers';
import { calculateCapacity } from '../shared/capacityHelpers';
import { calculateTotalEffectiveCapacity } from '../shared/raw2Useable';
import { calculateSystemReliability } from '../shared/durabilityCalculator';

import { FLASH_NODE_MODELS } from '../../utils/partNumbers';
import { getProductPower, getProductWeight, getProductMaxPower } from '../../utils/configUtilities';
import type { AfaStorageBlock } from '../../domain/StorageBlock';
import type { MetricsContext, ProductDb, MetricsDb } from '../types';

/**
 * CONF-258: the AFA throughput factors, separated from node count so the
 * metrics layer can expose a per-node rating alongside the system totals.
 * @param afa - AFA storage block from SystemConfiguration
 * @param ctx - Cross-box inputs (quarter, network, flash node model)
 * @param metricsDb - Metrics database with performance data
 */
export function afaThroughputFactors(afa: AfaStorageBlock, ctx: MetricsContext, metricsDb: MetricsDb) {
  const { encodingScheme: afaEncodingScheme, compressionRatio: afaCompressionRatio } = afa;

  const quarter = ctx.quarter;
  const compressionState = getCompressionState(afaCompressionRatio);
  const writeEfficiency = calculateWriteEfficiency(afaEncodingScheme);

  // CONF-93: flash throughput is min(card ceiling, protocol rate).
  // CONF-210: the Dual CPU flash node reads its own table (network.nodeModels).
  // CONF-221: a QLC part adds the media ceiling (network.mediaCeiling) as a
  // third min — 40 GB/s write per node whatever the card, protocol or model.
  const { read, write } =
    resolveNetworkThroughput(metricsDb, ctx.networkOption, ctx.networkProtocol, compressionState, quarter, ctx.afaNodeModel, ctx.afaSsdMediaType);

  return { read, write, writeEfficiency };
}

/**
 * Calculate all AFA node-specific metrics
 * @param afa - AFA storage block from SystemConfiguration
 * @param ctx - Cross-box inputs (quarter, network, flash node model)
 * @param productDb - Product database
 * @param metricsDb - Metrics database with performance data
 * @returns AFA node metrics (flat, same keys as before)
 */
export default function calculateAfaNodeMetrics(
  afa: AfaStorageBlock,
  ctx: MetricsContext,
  productDb: ProductDb,
  metricsDb: MetricsDb,
) {
  const {
    nodeCount: afaNodeCount,
    vpodSsdCapacity: afaVpodSsdCapacity,
    encodingScheme: afaEncodingScheme,
    spares: afaSpares,
    compressionRatio: afaCompressionRatio,
  } = afa;

  const ssdPerVpod = metricsDb.global.ssdPerVpod;

  // ========== CAPACITY CALCULATIONS ==========
  const totalAfaSsdCapacity = calculateCapacity(afaNodeCount, ssdPerVpod, afaVpodSsdCapacity);
  const totalAfaRawCapacity = totalAfaSsdCapacity;

  const afaCapacityConfig = {
    nodeCount: afaNodeCount,
    jbodSize: 0,
    vpodHddCapacity: 0,
    vpodSsdCapacity: afaVpodSsdCapacity,
    veloCount: 0,
    veloSsdCapacity: 0,
    vpodsPerNode: productDb.vpodsPerNode
  };

  const afaCapacityResults = calculateTotalEffectiveCapacity(
    afaCapacityConfig,
    afaEncodingScheme,
    afaSpares
  );

  const afaVpodUseableCapacity = afaCapacityResults.vpodUseableCapacity;
  const afaEffectiveCapacity = afaCapacityResults.totalEffectiveCapacity;
  const afaCompressedEffectiveCapacity = afaEffectiveCapacity * afaCompressionRatio;

  // ========== PERFORMANCE CALCULATIONS ==========
  // CONF-258: factors extracted to afaThroughputFactors so the metrics layer
  // can also derive a per-node throughput basis from the same values.
  const f = afaThroughputFactors(afa, ctx, metricsDb);
  const writeEfficiency = f.writeEfficiency;
  const afaReadTransferRatePerNode = f.read;
  const afaWriteTransferRatePerNode = f.write;

  // Total throughput across all AFA nodes
  // MORK TODO : Verify if throughput is accurate for "Uncompressed" vs "Compressed" states - rounding?  // TEST w/ 3 nodes and 19 nodes
  const afaReadTransferRate = calculateThroughput(afaNodeCount, afaReadTransferRatePerNode);
  const afaWriteTransferRate = writeEfficiency * calculateThroughput(afaNodeCount, afaWriteTransferRatePerNode);

  // IOPS from metricsDb
  const afaIops = calculateIops(afaNodeCount, metricsDb.global.afaIopsPerNode);

  // ========== RELIABILITY CALCULATIONS ==========
  const afaReliabilityMetrics = calculateSystemReliability({
    hardwareVendor: ctx.hardwareVendor,
    jbodSize: 78,  // AFA uses standard 78-drive equivalent       // MORK TODO: This is a magic number...
    nodeCount: afaNodeCount,
    vpodHddCapacity: 0,
    vpodSsdCapacity: afaVpodSsdCapacity,
    encodingScheme: afaEncodingScheme
  });

  const afaDurabilityNines = afaReliabilityMetrics.durabilityNines;
  const afaAvailabilityNines = afaReliabilityMetrics.availabilityNines;

  // ========== PHYSICAL CALCULATIONS ==========
  // CONF-210: power and weight come from the SKU the flash node model books,
  // the same table the BOM reads, so the two cannot disagree.
  const afaStorageNodePartNumber = FLASH_NODE_MODELS[ctx.afaNodeModel].partNumber;

  const afaVpodNominalPower = afaNodeCount * getProductPower(productDb, afaStorageNodePartNumber);
  const afaVpodMaxPower = afaNodeCount * getProductMaxPower(productDb, afaStorageNodePartNumber);
  const afaVpodWeight = afaNodeCount * getProductWeight(productDb, afaStorageNodePartNumber);
  const afaVpodRU = afaNodeCount * ctx.rackHeights.afa;


  // ========== RETURN ALL METRICS ==========
  return {
    // Capacity
    totalAfaSsdCapacity,
    totalAfaRawCapacity,
    afaVpodUseableCapacity,
    afaEffectiveCapacity,
    afaCompressedEffectiveCapacity,

    // Performance
    afaIops,
    afaReadTransferRate,        // MORK TODO : This eventually should be renamed to afaNvmeReadTransferRate to match hybrid naming and system metrics consistency
    afaWriteTransferRate,       // MORK TODO : This eventually should be renamed to afaNvmeWriteTransferRate to match hybrid naming and system metrics consistency

    // Reliability
    afaDurabilityNines,
    afaAvailabilityNines,

    // Physical
    afaVpodNominalPower,
    afaVpodMaxPower,
    afaVpodWeight,
    afaVpodRU,
  };
}
